import {
  ApiError,
  acceptInvitation,
  acceptInvitationWithNewAccount,
  useGetMe,
} from '@myrentalmate/api-client';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';

import { apiErrorMessage } from '../../lib/apiError';
import { brand } from '../../lib/brand';
import { useFragmentToken } from '../../lib/useFragmentToken';
import { SignInPage } from '../auth/SignInPage';

/** /invite#<token>. Needs a click, because email scanners open links on their own. */
export function InviteAcceptPage() {
  const token = useFragmentToken();
  const me = useGetMe({ query: { retry: false } });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [signingIn, setSigningIn] = useState(false);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await queryClient.invalidateQueries();
      navigate('/', { replace: true });
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  function create(event: FormEvent) {
    event.preventDefault();
    void run(() => acceptInvitationWithNewAccount({ token, display_name: name, password }));
  }

  // A signed-in person with no business yet gets a 403 from /me, and is signed in all the same.
  const signedOut = me.error instanceof ApiError && me.error.status === 401;

  if (signedOut && signingIn) return <SignInPage />;

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 440, width: '100%' }}>
        <Stack spacing={2}>
          <Typography variant="h4" sx={{ textAlign: 'center' }}>
            {brand.name}
          </Typography>
          {!token ? (
            <Typography color="text.secondary">
              This invitation isn't valid. Ask for a new one.
            </Typography>
          ) : me.isPending ? (
            <CircularProgress aria-label="Loading" sx={{ alignSelf: 'center' }} />
          ) : signedOut ? (
            <Stack component="form" spacing={2} onSubmit={create}>
              <Typography color="text.secondary">
                You've been invited. Create your account to join.
              </Typography>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                label="Your Name"
                required
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <TextField
                label="Choose a Password"
                type="password"
                required
                autoComplete="new-password"
                helperText="At least 12 characters"
                slotProps={{ htmlInput: { minLength: 12 } }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button type="submit" variant="contained" size="large" disabled={busy}>
                Create account and join
              </Button>
              <Button variant="text" onClick={() => setSigningIn(true)}>
                I already have an account
              </Button>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Typography color="text.secondary">
                You've been invited to join a business. Accept to add it to your account.
              </Typography>
              {error && <Alert severity="error">{error}</Alert>}
              <Button
                variant="contained"
                size="large"
                disabled={busy}
                onClick={() => void run(() => acceptInvitation({ token }))}
              >
                Accept invitation
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
